"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { PreviewTemplateModel } from "@/app/models/PreviewTemplateModel";
import mainStyle from "@/app/styles/app.module.css";
import images from "@/app/constants/images";
import { ListingTemplateModel } from "@/app/models/ListingTemplateModel";
import WeddingCard from "@/app/components/listing/template";
import Breadcrumbs from "@/app/components/breadcrumbs";
import { useAuth } from "@/app/login/login";
import LoginPopup from "@/app/login/login";
import { trackEvent, Events, Sources } from "@/app/helpers/analytics";
import { openWhatsAppWithTracking } from "@/app/helpers/trackingService";
import strings from "@/app/constants/strings";
type Props = {
  template: PreviewTemplateModel | null;
  relatedTemplates: ListingTemplateModel[];
};
function useWindowSize() {
  // Initialize with a default width that will be immediately updated
  const [windowSize, setWindowSize] = useState({
    width: 1024, // Default to desktop size to prevent flicker on initial render
  });

  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Get the actual window width
      setWindowSize({
        width: window.innerWidth,
      });
    }

    // Add event listener
    if (typeof window !== "undefined") {
      // Initialize with the current window width
      handleResize();

      // Listen for future resize events
      window.addEventListener("resize", handleResize);

      // Remove event listener on cleanup
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []); // Empty array ensures effect is only run on mount and unmount

  return windowSize;
}

declare global {
  function gtag(...args: any[]): void;
}

/** Convert Vimeo URL (e.g. https://vimeo.com/1166405771/641623c8ed) to embed URL */
function getVimeoEmbedUrl(url: string): string | null {
  if (!url || !url.includes("vimeo.com")) return null;
  const match = url.match(/vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/);
  if (!match) return null;
  const videoId = match[1];
  const hash = match[2];
  return `https://player.vimeo.com/video/${videoId}${hash ? `?h=${hash}` : ""}${hash ? "&" : "?"}autoplay=1&muted=1&dnt=1`;
}

export default function PreviewPage({ template, relatedTemplates }: Props) {
  if (!template?.img_link) {
    return (
      <p className="text-red-500 text-center mt-10">
        Error: Image source not found.
      </p>
    );
  }

  const imageList = template.images;
  const [selectedImage, setSelectedImage] = useState<string>(template.img_link);
  const [isMobile, setIsMobile] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const isMobileVideo = !!(template?.isVideo && isMobile);
  const handleCardClick = (template: ListingTemplateModel) => {
    const slug = template.title
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9\-]/g, "");
    const path = `/templates/${slug}/${template.id * 55}`;

    router.prefetch(path);
    router.push(path);
  };
  useEffect(() => {
    setHydrated(true);
  }, []);

  // Handle successful login - close popup if user becomes authenticated
  useEffect(() => {
    if (showLoginPopup && user && !authLoading) {
      // User has logged in, close the popup
      // The auth callback will handle the redirect
      setShowLoginPopup(false);
    }
  }, [user, authLoading, showLoginPopup]);

  // No dynamic height: container uses fixed aspect ratio to prevent layout shifts

  // isMobile is determined on the server and passed as a prop to avoid hydration flip

  // Handle visibility change to pause video when switching tabs
  useEffect(() => {
    if (typeof window !== "undefined") {
      const checkMobile = () => setIsMobile(window.innerWidth < 768);
      checkMobile();
      window.addEventListener("resize", checkMobile);
      return () => window.removeEventListener("resize", checkMobile);
    }
  }, []);
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (template?.isVideo && !isVideoLoaded) {
      // Fallback: Show video after 3 seconds even if onLoad doesn't fire
      timeoutId = setTimeout(() => {
        setIsVideoLoaded(true);
      }, 3000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [template?.isVideo, isVideoLoaded]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden" && videoRef.current) {
        videoRef.current.pause();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
  const getIsPopular = (index: number) => {
    return index < 3; // First 3 templates are popular
  };
  const features = [
    { label: "Download PDF", icon: images.downloadPdfIcon },
    { label: "Download Image", icon: images.downloadImageIcon },
    { label: "Share", icon: images.shareIcon },
  ];

  // Default video features
  const defaultVideoFeatures = [
    { label: "Delivered within 2 days", icon: images.order },
    { label: "Full HD quality video", icon: images.hdIcon },
    { label: "Dedicated support team", icon: images.supportTeamIcon },
    { label: "Song can be changed", icon: images.loveIcon },
  ];

  // Use video features for video templates, regular features for image templates
  const displayFeatures = template.isVideo ? defaultVideoFeatures : features;

  const handleAppDownload = () => {
    gtag("event", "app_download", {
      event_category: "engagement",
      event_label: "mobile_app",
      value: 1,
    });

    setTimeout(() => {
      router.prefetch("/app");
      router.push("/app");
    }, 100);
  };
  const { width } = useWindowSize();
  const getGridTemplateColumns = (templateCount: number) => {
    // Make sure we have a valid width

    if (typeof width !== "number" || width === 0) {
      return "repeat(auto-fit, minmax(250px, 1fr))"; // Default to desktop size
    }
    if (templateCount < 3 && width > 640) {
      console.log("width", width);
      return `repeat(${templateCount}, 250px)`;
    }
    if (templateCount == 3 && width > 640) {
      console.log("width", width);
      return `repeat(${templateCount}, 140px)`;
    }
    // console.log("Current width in getGridTemplateColumns:", width);

    if (width < 640) return "repeat(auto-fit, minmax(140px, 1fr))";
    if (width < 768) return "repeat(auto-fit, minmax(200px, 1fr))";
    if (width < 1024) return "repeat(auto-fit, minmax(250px, 1fr))";
    return "repeat(auto-fit, minmax(250px, 1fr))";
  };
  const moveToListing = () => {
    // If video template, navigate to video page, otherwise to culture page
    const path = template?.isVideo
      ? `/wedding-invitations/video`
      : `/wedding-invitations/${template?.culture?.toLowerCase()}`;
    router.prefetch(path);
    router.push(path);
  };
  const moveToEditing = () => {
    const path = `/app`;
    // const slug = template.title
    //   .toLowerCase()
    //   .replace(/\s+/g, "-")
    //   .replace(/[^a-z0-9\-]/g, "");
    // const path = `/design/${slug}/${template.id * 55}`;

    router.prefetch(path);
    router.push(path);
  };
  const moveToVideoEditing = () => {
    // Track customize click event
    trackEvent(Events.customizeClickWeb, {
      product_type: "video_invitation",
      platform: "web",
      card_id: template.id, // template id user clicked
      login_state: user ? "logged_in" : "logged_out",
    });

    // Check if user is logged in
    if (!authLoading && !user) {
      // User is not logged in, show login popup
      // Store the target path in sessionStorage for redirect after login
      if (typeof window !== "undefined") {
        const targetPath = `/customize/${template.id}`;
        sessionStorage.setItem("onLoginPagePath", targetPath);
      }
      setShowLoginPopup(true);
      return;
    }

    // User is logged in, navigate directly
    // Tailwind classes will handle mobile/desktop visibility automatically
    const path = `/customize/${template.id}`;
    router.prefetch(path);
    router.push(path); //
  };
  // const handleOrderOnWhatsApp = () => {
  //   const phoneNumber = "918708328579";
  //   const templateId = template.id ? Math.floor(template.id / 55) : "";

  //   const message = `Hi! I would like to order this video invitation${
  //     templateId ? ` (ID: ${templateId})` : ""
  //   }. Can you help me with the process?`;

  //   const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
  //     message
  //   )}`;

  //   // ✅ TypeScript-safe dataLayer push (NO global changes)
  //   if (typeof window !== "undefined") {
  //     const w = window as unknown as {
  //       dataLayer?: Array<Record<string, any>>;
  //     };

  //     w.dataLayer = w.dataLayer || [];
  //     w.dataLayer.push({
  //       event: "whatsapp_click",
  //       template_id: templateId,
  //       page_path: window.location.pathname,
  //     });
  //   }

  //   // navigate after tracking
  //   window.open(whatsappUrl, "_blank");
  // };///////

  const handleOrderOnWhatsApp = () => {
    // Track WhatsApp click event
    trackEvent(Events.whatsappClick, {
      template_id: template?.id,
      source: Sources.templatePreview,
    });

    openWhatsAppWithTracking({
      cardId: template?.id ? String(template.id) : undefined,
      customMessage: "Hi! I would like to order this wedding invitation. Can you help me with the process?",
    });
  };

  function capitalize(str: string) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  function stripHtml(html: string) {
    if (!html) return "";
    return html
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
  function truncateAtWord(text: string, limit: number) {
    if (text.length <= limit) return text;
    const sliced = text.slice(0, limit);
    const cut = sliced.replace(/\s+\S*$/, "");
    return cut.endsWith(".") ? cut.slice(0, -1) + "…" : cut + "…";
  }
  const altText =
    capitalize(template.title) +
    " " +
    capitalize(template.culture) +
    " Wedding Invitation Card";

  const plainDescription = stripHtml(template.description || "");
  const descriptionWords = plainDescription.split(/\s+/).filter(Boolean);
  const shouldShowReadMore = descriptionWords.length > 35;
  const truncatedDescription = descriptionWords.slice(0, 35).join(" ");

  const categoryPath = `/wedding-invitations`;

  return (
    <div className={mainStyle.containerMdTopPadding}>
      <div className="hidden md:block">
        <Breadcrumbs
          items={[
            { label: "Wedding Invites", url: categoryPath },
            { label: "Product page" },
          ]}
        />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[700fr_640fr] gap-4 md:gap-4">
        <div className="flex flex-col items-center">
          {/* Mobile variant */}
          <div className="block md:hidden relative aspect-[595/842] w-full bg-[#F4F4F4] ">
            <div className="absolute inset-0 flex items-center justify-center px-[32px] py-8">
              {template.isVideo && template.videos?.preview ? (
                (() => {
                  const embedUrl = getVimeoEmbedUrl(template.videos.preview);
                  return embedUrl ? (
                    <div className="relative h-full w-full">
                      {!isVideoLoaded && (
                        <div className="absolute inset-0 flex items-center justify-center z-10 bg-[#F4F4F4]">
                          <img
                            src={selectedImage}
                            alt=""
                            className="h-full w-full object-contain blur-[2px] opacity-50"
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-10 h-10 border-4 border-gray-300 border-t-[#FE406C] rounded-full animate-spin" />
                          </div>
                        </div>
                      )}
                      <iframe
                        src={embedUrl}
                        className={`h-full w-full object-contain transition-opacity duration-200 ${
                          isVideoLoaded ? "opacity-100" : "opacity-0"
                        }`}
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        title="Video preview"
                        loading="eager"
                        onLoad={() => setIsVideoLoaded(true)}
                      />
                    </div>
                  ) : null;
                })()
              ) : !template.isVideo ? (
                <img
                  src={selectedImage}
                  alt={altText}
                  className="h-full w-full object-contain  pointer-events-none"
                  onContextMenu={(e) => e.preventDefault()}
                />
              ) : null}
            </div>
          </div>

          {/* Desktop variant */}
          <div className="hidden md:block relative aspect-[700/500] w-full bg-[#F4F4F4]  mb-10">
            {template.isVideo && template.videos?.preview ? (
              (() => {
                const embedUrl = getVimeoEmbedUrl(template.videos.preview);
                return embedUrl ? (
                  <div className="absolute inset-0 m-auto h-[94%] w-full">
                    {!isVideoLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center z-10 bg-[#F4F4F4]">
                        <img
                          src={selectedImage}
                          alt=""
                          className="h-auto w-auto max-h-full max-w-full object-contain blur-[2px] opacity-50"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 border-4 border-gray-300 border-t-[#FE406C] rounded-full animate-spin" />
                        </div>
                      </div>
                    )}
                    <iframe
                      src={embedUrl}
                      className={`h-full w-full object-contain transition-opacity duration-200 ${
                        isVideoLoaded ? "opacity-100" : "opacity-0"
                      }`}
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                      title="Video preview"
                      loading="eager"
                      onLoad={() => setIsVideoLoaded(true)}
                    />
                  </div>
                ) : null;
              })()
            ) : !template.isVideo ? (
              <img
                src={selectedImage}
                alt={altText}
                className="absolute inset-0 m-auto h-[94%] w-auto object-contain  pointer-events-none"
                onContextMenu={(e) => e.preventDefault()}
              />
            ) : null}
          </div>

          {/* Thumbnails */}
          {!template.isVideo && imageList.length > 1 && (
            <div className="w-full overflow-x-auto mt-4 mb-10">
              <div className="flex gap-3">
                {imageList.map((img: string, idx: number) => (
                  <div
                    key={idx}
                    className={`relative aspect-[500/500] flex-shrink-0 w-[28%] bg-[#F4F4F4]  ${
                      selectedImage === img
                        ? "border-[#FE406C] border-2"
                        : "border-gray-300"
                    }`}
                    onClick={() => setSelectedImage(img)}
                  >
                    <img
                      src={img}
                      alt={`Thumbnail ${idx + 1}`}
                      className="absolute inset-0 m-auto h-[84%] w-auto object-contain pointer-events-none"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Right Column: Info Section */}
        <div className="flex flex-col justify-start gap-4 md:gap-4">
          <h1
            className="font-semibold text-[#0B214A]"
            style={{
              fontSize: "clamp(1.125rem, 1.2vw + 0.5rem, 1.75rem)",
            }}
          >
            {template.title}
          </h1>

          <div className="flex items-center space-x-2">
            <span className="text-[#CB3356] text-2xl font-bold">
              {`₹${template.price}`}
            </span>

            <span className="text-[#A9A9A9] line-through text-xl font-semibold">
              ₹{template.price.toFixed(0) * 1.5}
            </span>
          </div>
          <div className="flex  flex-col md:hidden justify-start gap-3">
            {!template.isVideo && (
              <button
                onClick={handleOrderOnWhatsApp}
                className="block md:hidden w-full bg-[#FE406C] text-white py-3 px-8 text-lg rounded-full hover:bg-[#E03056] transition-all duration-300 shadow-md flex items-center justify-center gap-2"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
                    fill="currentColor"
                  />
                </svg>
                Order on WhatsApp
              </button>
            )}
            {!template.isVideo && (
              <button
                onClick={handleOrderOnWhatsApp}
                className="block md:hidden w-full bg-[#25D366] text-white py-3 px-8 text-lg rounded-full hover:bg-[#1DA851] transition-all duration-300 shadow-md flex items-center justify-center gap-2"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="flex-shrink-0"
                >
                  <path
                    d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
                    fill="currentColor"
                  />
                </svg>
                Order on WhatsApp
              </button>
            )}
            <div className="w-full h-px "></div>
          </div>
          {/* Mobile: Why couples trust us */}
          <div className="flex flex-col justify-start gap-2 md:hidden">
            <h2 className="text-[14px] leading-[150%] font-medium uppercase text-[#222222]">
              Why couples trust us
            </h2>
            <div
              className="w-full h-px "
              style={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }}
            ></div>
          </div>

          <div className="md:hidden">
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#FE406C"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-5 h-5 flex-shrink-0"
                >
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
                <div className="flex flex-col">
                  <span
                    className="font-regular text-[#453C3C]"
                    style={{
                      fontSize: "clamp(0.875rem, 1vw + 0.25rem, 1rem)",
                    }}
                  >
                    Rated 4.7 by 400+ Couples
                  </span>
                  <a
                    href={strings.appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-regular text-[#453C3C] hover:text-[#FE406C] transition-colors underline"
                    style={{
                      fontSize: "clamp(0.875rem, 1vw + 0.25rem, 1rem)",
                    }}
                  >
                    (Verified on the App Store)
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <img
                  src={images.loveIcon.src}
                  alt={images.loveIcon.alt}
                  className="w-5 h-5 object-contain"
                />
                <span
                  className="font-regular text-[#453C3C]"
                  style={{
                    fontSize: "clamp(0.875rem, 1vw + 0.25rem, 1rem)",
                  }}
                >
                  Trusted by Indian couples
                </span>
              </li>
            </ul>
          </div>

          {/* Desktop: Description */}
          <div className="hidden md:flex flex-col justify-start gap-2">
            <h2 className="text-[14px] leading-[150%] font-medium uppercase text-[#222222] md:normal-case md:font-semibold md:text-[#0B214A] md:leading-normal md:text-lg">
              Description
            </h2>
            <div
              className="w-full h-px "
              style={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }}
            ></div>
          </div>

          <div className="hidden md:block">
            <div
              className="text-[16px] leading-[140%] font-light text-[#565656] md:font-normal md:text-[#453C3C] [&_a]:text-[#FE406C] [&_a]:underline [&_a]:hover:opacity-80"
              dangerouslySetInnerHTML={{ __html: template.description || "" }}
            />
          </div>

          <div className="flex flex-col justify-start gap-2 md:gap-2">
            <h2 className="text-[14px] leading-[150%] font-medium uppercase text-[#222222] md:normal-case md:font-semibold md:text-[#0B214A] md:leading-normal md:text-lg">
              What You'll Get
            </h2>
            <div
              className="w-full h-px"
              style={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }}
            ></div>
          </div>
          <ul className="space-y-3 text-gray-700">
            {displayFeatures.map((item: any, idx: number) => (
              <li key={idx} className="flex items-center gap-3">
                <img
                  src={item.icon?.src || item.icon}
                  alt={item.icon?.alt || item.label}
                  className="w-5 h-5 object-contain"
                />
                <span
                  className="font-regular text-[#453C3C]"
                  style={{
                    fontSize: "clamp(0.875rem, 1vw + 0.25rem, 1rem)",
                  }}
                >
                  {item.label}
                </span>
              </li>
            ))}
          </ul>

          {/* Desktop Download Button */}
          <button
            onClick={
              template.isVideo ? handleOrderOnWhatsApp : handleOrderOnWhatsApp
            }
            className="hidden md:flex w-full bg-[#FE406C] text-white py-3 px-8 text-lg rounded-full hover:bg-[#E03056] transition-all duration-300 shadow-md items-center justify-center gap-2"
          >
            {template.isVideo && (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-shrink-0"
                style={{ display: "block" }}
              >
                <path
                  d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
                  fill="currentColor"
                />
              </svg>
            )}
            <span>
              {template.isVideo ? "Order on WhatsApp" : "Order on WhatsApp"}
            </span>
          </button>
        </div>
      </div>

      <div className="mb-10"></div>
      {/* You might also like Section */}
      <div className="">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[14px] leading-[150%] font-medium uppercase text-[#222222] md:normal-case md:font-semibold md:text-[#0B214A] md:leading-normal md:text-lg mb-2">
            You might also like
          </h2>
        </div>
        {relatedTemplates && relatedTemplates.length > 0 && (
          <div className="grid gap-4 sm:gap-8 mb-10">
            <div
              className="grid gap-4 sm:gap-8"
              style={{
                gridTemplateColumns: getGridTemplateColumns(
                  relatedTemplates.length
                ),
              }}
            >
              {relatedTemplates.map((template, index) => (
                <WeddingCard
                  key={template.id || index}
                  template={template}
                  index={index}
                  isPopular={getIsPopular(index)}
                  onCardClick={handleCardClick}
                />
              ))}
            </div>
          </div>
        )}
        <button
          onClick={moveToListing}
          className="block md:hidden w-full bg-[#FE406C] text-white text-[16px] py-3 px-8 text-lg rounded-full hover:bg-[#E03056] transition-all duration-300 shadow-md mb-10"
        >
          {template?.isVideo
            ? "View more Wedding Invites"
            : `View more ${template?.culture || "Hindu"} Wedding Invites`}
        </button>
      </div>

      {/* Mobile Sticky WhatsApp CTA for video templates */}
      {hydrated && isMobile && template.isVideo && (
        <div className="fixed bottom-0 left-0 w-full px-4 py-3 z-50 bg-white" style={{ boxShadow: '0 -2px 10px rgba(0,0,0,0.1)' }}>
          <button
            onClick={handleOrderOnWhatsApp}
            className="w-full bg-[#20B558] text-white py-3.5 text-lg rounded-full shadow-md hover:bg-[#1BA04D] transition-all duration-300 flex items-center justify-center gap-2 font-bold mb-1"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="flex-shrink-0"
            >
              <path
                d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
                fill="currentColor"
              />
            </svg>
            Order on WhatsApp
          </button>
          <div className="text-center text-xs text-gray-500 font-medium">Quick delivery • Easy ordering • Trusted by 10,000+ couples</div>
        </div>
      )}
      {/* Spacer for sticky button */}
      {isMobile && template.isVideo && <div className="h-24 md:hidden" />}

      {/* Login Popup */}
      {showLoginPopup && (
        <LoginPopup
          onClose={() => {
            setShowLoginPopup(false);
          }}
        />
      )}
    </div>
  );
}
